import type {
  AccessNodePathEntry,
  AccessRequestPublic,
  OtherAccessRequestPublic,
} from '@accounts/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import { STR } from '../../i18n';
import {
  ApiError,
  cancelMyAccessRequest,
  cancelMyOtherRequest,
  listMyAccessRequests,
  listMyApplications,
  listMyOtherRequests,
  listMyRequestableCatalog,
} from '../../lib/api';
import { formatDate } from '../../lib/format';
import { queryKeys } from '../../lib/queryKeys';
import { showToast } from '../../lib/toast';
import { Callout, Chip, type ChipTone, Skeleton } from '../../ui/Primitives';
import { quietButtonStyle, rowActionStyle } from '../../ui/styles';
import { REQUEST_STATUS_TONE, itemLabel, requestStatusLabel } from '../admin/access/accessFormat';

import { RequestHistory } from './RequestHistory';

/**
 * MY ACCESS REQUESTS — the launcher section the FINAL demo adds between Applications and
 * Announcements (spec row 3): every request the signed-in person has made, newest first,
 * with what it means for them now, and Cancel on a pending one.
 *
 * A suite app ends on the launcher ("Granted" when it is there now, read from the launcher's
 * own applications list by id); an outside system only ever shows its status here, because
 * the person signs in to it directly and there is no self-read of access records.
 *
 * Other requests (spec row 13, "Something else") list beside the catalog ones, newest
 * first, from their own read. Every request has a History (spec row 14): its dates and the
 * answer emails Accounts wrote to the person.
 *
 * `focus` is set when the bell sends somebody here (`?requests=open`, or a link naming the
 * request): the section scrolls into view, and `openId` opens that request's History.
 */

interface RowView {
  chipTone: ChipTone;
  chipLabel: string;
  line: string;
}

function viewOf(request: AccessRequestPublic, heldApps: ReadonlySet<string>): RowView {
  const base = {
    chipTone: REQUEST_STATUS_TONE[request.status],
    chipLabel: requestStatusLabel(request.status),
  };
  switch (request.status) {
    case 'pending':
      return { ...base, line: STR.access.request.linePending };
    case 'denied': {
      const reason = request.decisionNote?.trim() ?? '';
      return {
        ...base,
        line: reason === '' ? STR.access.request.lineDeniedNoReason : STR.access.request.lineDenied(reason),
      };
    }
    case 'cancelled':
      return { ...base, line: STR.access.request.lineCancelled };
    case 'approved': {
      const applicationId = request.catalogItem.applicationId;
      if (applicationId === null) return { ...base, line: STR.access.request.lineApprovedOutside };
      if (heldApps.has(applicationId)) {
        return {
          chipTone: 'positive',
          chipLabel: STR.access.request.granted,
          line: STR.access.request.lineGranted(request.catalogItem.name),
        };
      }
      return { ...base, line: STR.access.request.lineApprovedApp };
    }
  }
}

function otherViewOf(request: OtherAccessRequestPublic): RowView {
  const base = {
    chipTone: REQUEST_STATUS_TONE[request.status],
    chipLabel: requestStatusLabel(request.status),
  };
  switch (request.status) {
    case 'pending':
      return { ...base, line: STR.otherRequests.linePending };
    case 'denied':
      return {
        ...base,
        line: STR.otherRequests.lineDeclined(
          request.decidedBy?.name ?? '',
          (request.declineReason ?? '').trim() === '' ? null : (request.declineReason ?? '').trim(),
        ),
      };
    case 'cancelled':
      return { ...base, line: STR.otherRequests.lineCancelled };
    case 'approved':
      return {
        ...base,
        line: STR.otherRequests.lineApproved(request.catalogItem?.name ?? request.what),
      };
  }
}

type MyRow =
  | { kind: 'catalog'; at: string; request: AccessRequestPublic }
  | { kind: 'other'; at: string; request: OtherAccessRequestPublic };

export function MyAccessRequests({ focus, openId }: { focus: boolean; openId: string | null }) {
  const queryClient = useQueryClient();
  const sectionRef = useRef<HTMLDivElement | null>(null);
  // Ids whose History the person toggled. The one the bell named starts open, so a toggle
  // flips it from open — derived on every render, so a later bell link opens its own.
  const [toggled, setToggled] = useState<ReadonlySet<string>>(new Set());
  const isHistoryOpen = (id: string) => toggled.has(id) !== (id === openId);

  const otherQuery = useQuery({
    queryKey: queryKeys.myOtherRequests,
    queryFn: listMyOtherRequests,
  });
  const cancelOther = useMutation({
    mutationFn: (id: string) => cancelMyOtherRequest(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.myOtherRequests });
      showToast(STR.access.request.cancelled);
    },
    onError: (error) => {
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
  });

  function toggleHistory(id: string) {
    setToggled((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const requestsQuery = useQuery({
    queryKey: queryKeys.myAccessRequests,
    queryFn: listMyAccessRequests,
  });
  const applicationsQuery = useQuery({
    queryKey: queryKeys.myApplications,
    queryFn: listMyApplications,
  });
  // Only for the row's place in the hierarchy ("17th Street · Amazon"); a request whose row
  // is no longer requestable still shows by its own name.
  const catalogQuery = useQuery({
    queryKey: queryKeys.myAccessCatalog,
    queryFn: listMyRequestableCatalog,
  });

  useEffect(() => {
    if (!focus) return;
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focus]);

  const cancelMutation = useMutation({
    mutationFn: (id: string) => cancelMyAccessRequest(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.myAccessRequests });
      void queryClient.invalidateQueries({ queryKey: queryKeys.myAccessCatalog });
      showToast(STR.access.request.cancelled);
    },
    onError: (error) => {
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
  });

  const heldApps = new Set((applicationsQuery.data?.data ?? []).map((app) => app.id));
  const paths = new Map<string, readonly AccessNodePathEntry[]>(
    (catalogQuery.data?.data ?? []).map((item) => [item.id, item.path] as const),
  );
  const rows: MyRow[] = [
    ...(requestsQuery.data?.data ?? []).map(
      (request): MyRow => ({ kind: 'catalog', at: request.createdAt, request }),
    ),
    // An Other read that failed leaves the catalog requests showing; it never hides them.
    ...(otherQuery.data?.data ?? []).map(
      (request): MyRow => ({ kind: 'other', at: request.createdAt, request }),
    ),
  ].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));

  let body: ReactNode;
  if (requestsQuery.isLoading) {
    body = (
      <div className="sec-row">
        <Skeleton style={{ height: 13, width: 220, borderRadius: 5 }} />
      </div>
    );
  } else if (requestsQuery.isError) {
    body = (
      <div style={{ padding: 12 }}>
        <Callout
          tone="danger"
          role="alert"
          style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
        >
          <span style={{ flex: 1, minWidth: 180 }}>{STR.errors.couldNotLoad}</span>
          <button
            type="button"
            className="tbtn"
            style={quietButtonStyle}
            onClick={() => {
              void requestsQuery.refetch();
            }}
          >
            {STR.common.tryAgain}
          </button>
        </Callout>
      </div>
    );
  } else if (rows.length === 0) {
    body = (
      <div className="nogrants" style={{ padding: '28px 24px' }}>
        <p className="b" style={{ marginTop: 0 }}>
          {STR.access.request.myEmpty}
        </p>
      </div>
    );
  } else {
    body = rows.map((row) => {
      const id = row.request.id;
      const history = isHistoryOpen(id);
      const historyButton = (
        <button
          type="button"
          className="tbtn"
          style={rowActionStyle}
          aria-expanded={history}
          onClick={() => {
            toggleHistory(id);
          }}
        >
          {history ? STR.answerEmails.hideHistory : STR.answerEmails.history}
        </button>
      );

      if (row.kind === 'other') {
        const request = row.request;
        const view = otherViewOf(request);
        const cancelling = cancelOther.isPending && cancelOther.variables === request.id;
        return (
          <div className="sec-row" key={`other:${request.id}`} style={{ flexWrap: 'wrap' }}>
            <div className="grow" style={{ minWidth: 0 }}>
              <div className="t">
                <span className="nm">{request.catalogItem?.name ?? request.what}</span>
                <Chip tone={view.chipTone} size="sm">
                  {view.chipLabel}
                </Chip>
                <span className="tx-subtle" style={{ fontSize: 11.5, fontWeight: 500 }}>
                  {request.catalogItem === null
                    ? STR.otherRequests.kind
                    : STR.otherRequests.askedAs(request.what)}
                </span>
              </div>
              <div className="note">{view.line}</div>
              {history ? (
                <RequestHistory
                  kind="other"
                  id={request.id}
                  askedAt={request.createdAt}
                  decidedAt={request.decidedAt}
                />
              ) : null}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {historyButton}
              {request.status === 'pending' ? (
                <button
                  type="button"
                  className="ghost"
                  style={rowActionStyle}
                  disabled={cancelOther.isPending}
                  onClick={() => {
                    cancelOther.mutate(request.id);
                  }}
                >
                  {cancelling ? STR.access.request.cancelling : STR.access.request.cancel}
                </button>
              ) : null}
            </div>
          </div>
        );
      }

      const request = row.request;
      const view = viewOf(request, heldApps);
      const path = paths.get(request.catalogItem.id) ?? [];
      const cancelling = cancelMutation.isPending && cancelMutation.variables === request.id;
      return (
        <div className="sec-row" key={request.id} style={{ flexWrap: 'wrap' }}>
          <div className="grow" style={{ minWidth: 0 }}>
            <div className="t">
              <span className="nm">{itemLabel(path, request.catalogItem.name)}</span>
              <Chip tone={view.chipTone} size="sm">
                {view.chipLabel}
              </Chip>
              <span className="tx-subtle" style={{ fontSize: 11.5, fontWeight: 500 }}>
                {request.catalogItem.applicationId === null
                  ? STR.access.request.outsideHint
                  : STR.access.request.suiteHint}
              </span>
            </div>
            <div className="note">{view.line}</div>
            <div className="note">
              {STR.access.request.asked(formatDate(request.createdAt))}
              {request.decidedAt === null ? null : (
                <>
                  {STR.accessCommon.pathSeparator}
                  {STR.access.request.decided(formatDate(request.decidedAt))}
                </>
              )}
            </div>
            {history ? (
              <RequestHistory
                kind="catalog"
                id={request.id}
                askedAt={request.createdAt}
                decidedAt={request.decidedAt}
              />
            ) : null}
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {historyButton}
            {request.status === 'pending' ? (
              <button
                type="button"
                className="ghost"
                style={rowActionStyle}
                disabled={cancelMutation.isPending}
                onClick={() => {
                  cancelMutation.mutate(request.id);
                }}
              >
                {cancelling ? STR.access.request.cancelling : STR.access.request.cancel}
              </button>
            ) : null}
          </div>
        </div>
      );
    });
  }

  return (
    <div className="c-sec" ref={sectionRef} style={{ scrollMarginTop: 16 }}>
      <span className="mlab">{STR.access.request.mySection}</span>
      <div className="cardx">{body}</div>
      <p className="tx-subtle" style={{ fontSize: 12, margin: '0 2px' }}>
        {STR.access.request.myFootnote}
      </p>
    </div>
  );
}
